/**
 * Builds Algolia `filters` string fragments for string facets.
 *
 * IMPORTANT: Algolia expects `attr:"value"` (or `(attr:"v1" OR attr:"v2")`),
 * not `attr:("value")` which causes parser errors.
 */

export function escapeAlgoliaFacetValueForDoubleQuotedFilter(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** One or more exact `location` facet values, OR-ed inside the group. */
export function buildAlgoliaLocationFacetFilter(locations: string[]): string | null {
  const cleaned = locations.map((l) => l.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  if (cleaned.length === 1) {
    const single = escapeAlgoliaFacetValueForDoubleQuotedFilter(cleaned[0]);
    return `location:"${single}"`;
  }
  const inner = cleaned
    .map((loc) => `location:"${escapeAlgoliaFacetValueForDoubleQuotedFilter(loc)}"`)
    .join(' OR ');
  return `(${inner})`;
}

/**
 * One or more exact `normalisedTitle` facet values, OR-ed inside the group — same shape as
 * buildAlgoliaLocationFacetFilter/buildAlgoliaFieldFacetFilter above. Accepting an array (not
 * just one title) matters here specifically: the search index still stores each job's raw,
 * un-merged title, so matching a job's known role-synonym group (see getSalaryRoleTitleVariants)
 * requires OR-ing across every variant string, not just the one title the current job happens to
 * use.
 */
export function buildAlgoliaNormalisedTitleFacetFilter(normalisedTitle: string | string[]): string {
  const titles = Array.isArray(normalisedTitle) ? normalisedTitle : [normalisedTitle];
  const cleaned = titles.map((t) => t.trim()).filter(Boolean);
  if (cleaned.length <= 1) {
    const single = escapeAlgoliaFacetValueForDoubleQuotedFilter(cleaned[0] ?? '');
    return `normalisedTitle:"${single}"`;
  }
  const inner = cleaned
    .map((t) => `normalisedTitle:"${escapeAlgoliaFacetValueForDoubleQuotedFilter(t)}"`)
    .join(' OR ');
  return `(${inner})`;
}

/** One or more exact `field` facet values (category alias variants), OR-ed inside the group. */
export function buildAlgoliaFieldFacetFilter(fieldValues: string[]): string | null {
  const cleaned = fieldValues.map((f) => f.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  if (cleaned.length === 1) {
    const single = escapeAlgoliaFacetValueForDoubleQuotedFilter(cleaned[0]);
    return `field:"${single}"`;
  }
  const inner = cleaned
    .map((f) => `field:"${escapeAlgoliaFacetValueForDoubleQuotedFilter(f)}"`)
    .join(' OR ');
  return `(${inner})`;
}
